import { Link } from "react-router-dom";
import BrandName from "@/components/BrandName";

const COMPANY = "OrbitRank";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="container py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold">
            <span className="w-7 h-7 rounded-md bg-primary/15 flex items-center justify-center">🚀</span>
            <BrandName />
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to home</Link>
        </div>
      </header>

      <main className="container max-w-3xl py-16">
        <h1 className="text-4xl font-bold mb-2">Privacy Notice</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: April 17, 2026</p>

        <div className="prose prose-invert max-w-none space-y-6 text-[15px] leading-relaxed text-muted-foreground">
          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">1. Who we are</h2>
            <p>
              This Privacy Notice explains how {COMPANY} ("OrbitRank", "we", "us") collects and processes
              personal data when you use our website and SaaS services (the "Service"). {COMPANY} is the
              data controller for the personal data described here.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">2. Personal data we collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">Account data:</strong> name, email address, password (hashed), and the website URL you connect.</li>
              <li><strong className="text-foreground">Business data:</strong> business type, location, target keywords, content you generate or publish through the Service.</li>
              <li><strong className="text-foreground">Usage data:</strong> pages visited, features used, log files, device identifiers, IP address, browser type.</li>
              <li><strong className="text-foreground">Support data:</strong> messages you send through our chat widget or support channels.</li>
              <li><strong className="text-foreground">Marketing data:</strong> email preferences and engagement (opens, clicks) where applicable.</li>
            </ul>
            <p>Payment data (card details, billing address, tax ID) is collected and processed directly by our Merchant of Record, Paddle. We do not receive or store your full payment card details.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">3. Why we use your data</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To create and manage your account (legal basis: performance of a contract).</li>
              <li>To deliver the Service — analyze your website, generate content, track rankings, build backlinks (contract).</li>
              <li>To prevent fraud, abuse, and security incidents (legitimate interest).</li>
              <li>To improve the Service and develop new features (legitimate interest).</li>
              <li>To provide customer support (contract / legitimate interest).</li>
              <li>To send service announcements and, where you have opted in, marketing communications (consent).</li>
              <li>To comply with legal obligations (legal obligation).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">4. Who we share data with</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-foreground">Paddle</strong> — our Merchant of Record for payments, subscription management, tax compliance, and invoicing.</li>
              <li><strong className="text-foreground">Infrastructure providers</strong> — hosting, database, and email delivery vendors that process data on our behalf under contract.</li>
              <li><strong className="text-foreground">Analytics providers</strong> — to understand product usage in aggregate.</li>
              <li><strong className="text-foreground">Professional advisers</strong> — legal, accounting, and compliance advisers when needed.</li>
              <li><strong className="text-foreground">Authorities</strong> — where required by law, court order, or to protect our rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">5. International transfers</h2>
            <p>
              Your data may be processed outside your country of residence, including in the United States.
              Where data is transferred out of the UK or EEA, we rely on appropriate safeguards such as the
              EU Standard Contractual Clauses or adequacy decisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">6. Data retention</h2>
            <p>
              We keep your personal data for as long as your account is active and for a reasonable period
              afterwards to comply with legal, tax, and accounting obligations. When data is no longer
              needed, we delete or anonymise it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">7. Your rights</h2>
            <p>Subject to applicable law, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your data.</li>
              <li>Restrict or object to certain processing.</li>
              <li>Receive your data in a portable format.</li>
              <li>Withdraw consent at any time (without affecting prior processing).</li>
              <li>Lodge a complaint with your local data protection authority.</li>
            </ul>
            <p>We respond to requests within one month.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">8. Security</h2>
            <p>
              We use appropriate technical and organisational measures, including encryption in transit,
              access controls, and regular security reviews, to protect personal data against unauthorised
              access, alteration, or loss.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">9. Cookies</h2>
            <p>
              We use essential cookies to keep you signed in and to remember your preferences. We may also
              use analytics cookies to understand how the Service is used. You can control cookies through
              your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">10. Contact</h2>
            <p>
              For privacy questions or to exercise your rights, contact {COMPANY} via the chat widget on
              our website or your account support channel.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        <div className="container flex flex-wrap justify-center gap-6">
          <Link to="/privacy-policy" className="hover:text-foreground">Privacy</Link>
          <Link to="/terms-of-service" className="hover:text-foreground">Terms</Link>
          <Link to="/refund-policy" className="hover:text-foreground">Refunds</Link>
        </div>
        <div className="mt-3">OrbitRank © 2026</div>
      </footer>
    </div>
  );
}
